import React, { MouseEvent, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Button } from '@progress/kendo-react-buttons';

import {
  resetEndDateTimeFilter,
  resetStartDateTimeFilter,
  setEndDateTimeFilter,
  setStartDateTimeFilter,
  setRefreshAlertsRequested,
} from '../../store/Alerts/AlertsActions';
import BasicDateTimePicker from '../BasicDateRangePicker';
import { ApplicationState } from '../../store';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useCurrentDateTime } from '../../store/Alerts/AlertsActions';
import { GetString } from '../StringResourceManager';
import { LocaleInterface, locales } from './GridLocale';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      '& > *': {
        margin: theme.spacing(2),
      },
    },
    paperGrid: {
      marginTop: theme.spacing(7),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
    },
    dateTime: {
      textAlign: 'center',
      color: '#23496B',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: 35,
    },
    dateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      margin: theme.spacing(2),
    },
    headerRowElement: {
      margin: theme.spacing(0.5),
    },
  })
);

export default function AlertsDateRangeSelector() {
  const classes = useStyles();

  const startDate: Date = useSelector(
    (state: ApplicationState) => state.alertsReducer.startDateTimeFilter
  );
  const endDate: Date = useSelector(
    (state: ApplicationState) => state.alertsReducer.endDateTimeFilter
  );

  const useCurrentDateTimeFilter: boolean = useSelector(
    (state: ApplicationState) => state.alertsReducer.useCurrentDateTimeFilter
  );

  const dispatch: Dispatch<any> = useDispatch();

  const handleStartDateTimeChanged = (date: Date) => {
    setStartDateTimeFilter(dispatch, date);
  };

  const handleEndDateTimeChanged = (date: Date) => {
    setEndDateTimeFilter(dispatch, date);
  };

  const handleClickRefresh = (event: MouseEvent<HTMLButtonElement>): void => {
    setRefreshAlertsRequested(dispatch, true);
  };

  const handleFilterEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    useCurrentDateTime(dispatch, event.target.checked);
  };

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    resetStartDateTimeFilter(dispatch);
    resetEndDateTimeFilter(dispatch);
    return () => {};
  }, []);

  return (
    <>
      <div className={classes.dateRangeContainer}>
        <div className={classes.headerRowElement}>
          <BasicDateTimePicker
            label={GetString('START_DATE_TIME')}
            initialDate={startDate}
            onDateChange={handleStartDateTimeChanged}
            disabled={false}
          />
        </div>
        <div className={classes.headerRowElement}>
          <BasicDateTimePicker
            label={GetString('END_DATE_TIME')}
            initialDate={endDate}
            onDateChange={handleEndDateTimeChanged}
            disabled={useCurrentDateTimeFilter}
          />
        </div>
        &nbsp;
        <div className={classes.headerRowElement}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleFilterEndDate}
                name="checkedB"
                color="primary"
                checked={useCurrentDateTimeFilter}
              />
            }
            label={GetString('FILTER_END_DATE_TIME')}
          />
          <Button svgIcon={arrowRotateCwIcon} onClick={handleClickRefresh}>
            {GetString('REFRESH')}
          </Button>
        </div>
      </div>
    </>
  );
}
